import EmailRounded from '@mui/icons-material/EmailRounded';
import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import SendRounded from '@mui/icons-material/SendRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { LoadingRows } from '../../components/states';
import { IconBadge, Panel } from '../../components/ui';
import { dateTime } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { Callout } from '../tenants/parts';
import {
  messagingKeys,
  usePlatformMail,
  usePlatformWhatsapp,
  usePlatformWhatsappActions,
  type PlatformMail,
} from './api';
import { WHATSAPP_GREEN } from './SendMessageDialog';

const title = (icon: ReactNode, text: string) => (
  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
    <IconBadge icon={icon} />
    <span>{text}</span>
  </Stack>
);

/**
 * Settings → PackBytes WhatsApp: the one number PackBytes sends from (activation codes, notices to
 * shops), linked by scanning a QR code. Never used by shops; they link their own.
 */
export function PackbytesWhatsappPanel() {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const [polling, setPolling] = useState(false);
  const status = usePlatformWhatsapp(polling);
  const actions = usePlatformWhatsappActions();
  const [unlink, setUnlink] = useState(false);
  const [to, setTo] = useState('');
  const linked = status.data?.status === 'LINKED';
  // Polls while linking (it stops by itself once linked); then say so.
  useEffect(() => {
    if (polling && linked) notify.success('PackBytes WhatsApp linked');
  }, [polling, linked, notify]);

  const s = status.data;
  return (
    <Panel
      title={title(<WhatsAppIcon />, 'PackBytes WhatsApp')}
      subtitle="One WhatsApp number for PackBytes only: activation codes and notices to shops. Shops link their own numbers."
      sx={{ maxWidth: 760 }}
    >
      {!s ? (
        <LoadingRows rows={2} />
      ) : (
        <Stack spacing={2}>
          {!s.available && (
            <Callout severity="info">WhatsApp is not available on this server.</Callout>
          )}
          {s.status === 'LINKED' ? (
            <Stack spacing={1}>
              <Typography>
                Linked: <strong>+{s.phone}</strong>
                {s.displayName ? ` (${s.displayName})` : ''}
              </Typography>
              {s.linkedAt && (
                <Typography variant="body2" color="text.secondary">
                  Since {dateTime(s.linkedAt)}
                </Typography>
              )}
            </Stack>
          ) : s.status === 'PAIRING' && s.qr ? (
            <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              <Box
                component="img"
                src={s.qr}
                alt="WhatsApp link QR code"
                sx={{ width: 240, height: 240, bgcolor: '#fff', p: 1, borderRadius: 2 }}
              />
              <Typography variant="body2">
                On PackBytes’ phone open WhatsApp → <strong>Linked devices</strong> →{' '}
                <strong>Link a device</strong> and scan this code. It refreshes by itself.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {s.lastError && <Callout severity="info">{s.lastError}</Callout>}
              <Typography variant="body2">No number linked yet.</Typography>
            </Stack>
          )}
          {canEdit && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'center' } }}
            >
              {linked ? (
                <>
                  <TextField
                    size="small"
                    label="Test to"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="0300 1234567"
                    slotProps={{ htmlInput: { inputMode: 'tel' } }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<SendRounded />}
                    loading={actions.test.isPending}
                    disabled={to.trim().length < 5}
                    onClick={() =>
                      actions.test.mutate(to.trim(), {
                        onSuccess: (r) => {
                          if (r.ok) notify.success('Test message sent');
                        },
                        onError: (e) => notify.error(e),
                      })
                    }
                  >
                    Send test
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<LinkOffRounded />}
                    onClick={() => setUnlink(true)}
                  >
                    Unlink
                  </Button>
                </>
              ) : (
                s.status !== 'PAIRING' && (
                  <Button
                    variant="contained"
                    startIcon={<QrCode2Rounded />}
                    disabled={!s.available}
                    loading={actions.connect.isPending}
                    onClick={() =>
                      actions.connect.mutate(undefined, {
                        onSuccess: () => setPolling(true),
                        onError: (e) => notify.error(e),
                      })
                    }
                    sx={{ bgcolor: WHATSAPP_GREEN, '&:hover': { bgcolor: '#178A43' } }}
                  >
                    Link WhatsApp
                  </Button>
                )
              )}
            </Stack>
          )}
          {actions.test.data && !actions.test.data.ok && (
            <Callout severity="error">{actions.test.data.error}</Callout>
          )}
        </Stack>
      )}
      <ConfirmDialog
        open={unlink}
        title="Unlink PackBytes WhatsApp?"
        body={`PackBytes stops sending from +${s?.phone ?? ''}. You can link a number again later.`}
        confirmLabel="Unlink"
        danger
        onConfirm={() => actions.disconnect.mutateAsync()}
        onClose={() => setUnlink(false)}
      />
    </Panel>
  );
}

const PRESETS = [
  {
    id: 'gmail',
    label: 'Gmail / Google Workspace',
    host: 'smtp.gmail.com',
    port: 465,
    security: 'SSL',
  },
  {
    id: 'm365',
    label: 'Microsoft 365 / Outlook',
    host: 'smtp.office365.com',
    port: 587,
    security: 'STARTTLS',
  },
  { id: 'godaddy', label: 'GoDaddy', host: 'smtpout.secureserver.net', port: 465, security: 'SSL' },
  { id: 'zoho', label: 'Zoho Mail', host: 'smtp.zoho.com', port: 465, security: 'SSL' },
] as const;

type Security = PlatformMail['security'];

/**
 * Settings → PackBytes email: PackBytes' own outgoing mail server. Sends staff invitations and
 * reminders (when switched on) and the emails PackBytes sends to shops from the admin console.
 */
export function PackbytesMailPanel() {
  const mail = usePlatformMail();
  return (
    <Panel
      title={title(<EmailRounded />, 'PackBytes email')}
      subtitle="PackBytes’ own outgoing mail server: staff invitations, reminders and emails to shops from the admin console. Shops set up their own servers."
      sx={{ maxWidth: 760 }}
    >
      {mail.data ? <MailForm key={mail.data.version} mail={mail.data} /> : <LoadingRows rows={3} />}
    </Panel>
  );
}

function MailForm({ mail }: { mail: PlatformMail }) {
  const { can } = useAuth();
  const canEdit = can('settings.manage');
  const notify = useNotify();
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(mail.enabled);
  const [host, setHost] = useState(mail.host ?? '');
  const [port, setPort] = useState(mail.port ? String(mail.port) : '');
  const [security, setSecurity] = useState<Security>(mail.security);
  const [username, setUsername] = useState(mail.username ?? '');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState(mail.fromName ?? 'PackBytes');
  const [fromAddress, setFromAddress] = useState(mail.fromAddress ?? '');
  const [replyTo, setReplyTo] = useState(mail.replyTo ?? '');
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const portNo = port ? Number(port) : null;
  const dirty =
    enabled !== mail.enabled ||
    host !== (mail.host ?? '') ||
    portNo !== mail.port ||
    security !== mail.security ||
    username !== (mail.username ?? '') ||
    password !== '' ||
    fromName !== (mail.fromName ?? 'PackBytes') ||
    fromAddress !== (mail.fromAddress ?? '') ||
    replyTo !== (mail.replyTo ?? '');

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/platform/settings/mail', {
          body: {
            enabled,
            host: host.trim() || null,
            port: portNo,
            security,
            username: username.trim() || null,
            fromName: fromName.trim() || null,
            fromAddress: fromAddress.trim() || null,
            replyTo: replyTo.trim() || null,
            version: mail.version,
            ...(password ? { password } : {}),
          },
        }),
      ),
    onSuccess: (saved) => {
      qc.setQueryData(messagingKeys.mail, saved);
      // Mail lives on the platform settings row: its version moved too.
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'], exact: true });
      notify.success('PackBytes email saved');
    },
    onError: (e) => {
      setError(e);
      if (e instanceof ApiError && e.code === 'VERSION_CONFLICT')
        void qc.invalidateQueries({ queryKey: messagingKeys.mail });
    },
  });
  const test = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/platform/settings/mail/test', {
          body: testTo.trim() ? { to: testTo.trim() } : {},
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: messagingKeys.mail }),
  });

  const preset = (id: string) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setHost(p.host);
    setPort(String(p.port));
    setSecurity(p.security);
  };

  return (
    <Stack spacing={2}>
      {error !== null && <Callout severity="error">{errorMessage(error)}</Callout>}
      {test.data && !test.data.ok && <Callout severity="error">{test.data.error}</Callout>}
      {mail.testError ? (
        <Callout severity="error">Last test failed: {mail.testError}</Callout>
      ) : mail.testedAt ? (
        <Typography variant="body2" color="text.secondary">
          Last test sent {dateTime(mail.testedAt)}.
        </Typography>
      ) : null}
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={!canEdit}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        }
        label={enabled ? 'On: PackBytes sends from this server' : 'Off'}
      />
      {canEdit && (
        <TextField
          select
          size="small"
          label="Provider"
          defaultValue=""
          onChange={(e) => preset(e.target.value)}
          sx={{ maxWidth: 320 }}
        >
          {PRESETS.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.label}
            </MenuItem>
          ))}
          <MenuItem value="other">Other SMTP server</MenuItem>
        </TextField>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="SMTP server"
          value={host}
          onChange={(e) => setHost(e.target.value)}
          disabled={!canEdit}
          sx={{ flex: 2 }}
        />
        <TextField
          label="Port"
          value={port}
          onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
          disabled={!canEdit}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          label="Security"
          value={security}
          onChange={(e) => setSecurity(e.target.value as Security)}
          disabled={!canEdit}
          sx={{ flex: 1 }}
        >
          <MenuItem value="SSL">SSL</MenuItem>
          <MenuItem value="STARTTLS">STARTTLS</MenuItem>
          <MenuItem value="NONE">None</MenuItem>
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="User name"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={!canEdit}
          autoComplete="off"
          sx={{ flex: 1 }}
        />
        <TextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={!canEdit}
          autoComplete="new-password"
          placeholder={mail.hasPassword ? 'Unchanged' : undefined}
          helperText="Gmail and Microsoft 365 need an app password."
          sx={{ flex: 1 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="From name"
          value={fromName}
          onChange={(e) => setFromName(e.target.value)}
          disabled={!canEdit}
          sx={{ flex: 1 }}
        />
        <TextField
          label="From address"
          value={fromAddress}
          onChange={(e) => setFromAddress(e.target.value)}
          disabled={!canEdit}
          sx={{ flex: 1 }}
        />
      </Stack>
      <TextField
        label="Reply-to (optional)"
        value={replyTo}
        onChange={(e) => setReplyTo(e.target.value)}
        disabled={!canEdit}
      />
      {canEdit && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <TextField
            size="small"
            label="Test to (default: you)"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'email' } }}
          />
          <Button
            variant="outlined"
            startIcon={<SendRounded />}
            loading={test.isPending}
            disabled={dirty}
            onClick={() =>
              test.mutate(undefined, {
                onSuccess: (r) => {
                  if (r.ok) notify.success('Test email sent');
                },
                onError: (e) => notify.error(e),
              })
            }
          >
            Send test
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
